import {OEFeatureAccessTypes} from '../react-oe/oe-types';
import {OEViewType, OEFreePremiumViewTypes} from '../lib/oe-types';
import oeURLParameterTool, {OEURLParameter} from '../lib/tools/oe-url-parameter-tool';
import {OEManualViewConfigFactory} from '../components/views/manual-view/oe-manual-view-model';
import {OEBottomWidgetToolbarWidgetType} from '../components/bars/bottom/oe-bottom-widget-toolbar-model';
import {OEMainMenuStdBtnType, OEMainMenuBtnLocation, OEMainMenuBtnConfigFactory} from '../components/bars/main-menu/oe-main-menu-model';
import OEMainMenuBtnAdapterView from '../components/bars/main-menu/button-adapters/oe-main-menu-btn-adapter-view';
import {OEHelpViewConfigFactory} from '../components/views/help-view/oe-help-view-model';
import {OEDefaultConfigFactory} from '../components/oe-default-configs';
import {OETarget, OEResourceTarget} from '../react-oe/oe-target';
import {OEVersion} from '../react-oe/oe-version';
import OEIconCodes from '../lib/oe-icon-codes';
import {OEFNStartSelectionModel} from '../components/views/flow-nav/start-selection/oe-fn-start-selection-model';
import {OEPresentationPlayableFlags} from '../components/controller/presentation/oe-presentation-model';
import {OEMediaCenterAutoplayMode} from '../components/controller/media-center/oe-media-center-model';
import {OEInfoMessageViewType} from '../components/views/info-message-view/oe-info-message-model';
import {OETargetMenuDefs} from '../components/elements/target-menu/oe-target-menu-defs';

class AppConfigHelper {

    /*
    params - optional, all fields also optional
    {
        targetMenuEntries:                          uiLayerConfig.targetMenu.entries,
        startViewType:                              uiLayerConfig.startView.type,
        infoHelp:                                   boolean (true) -> info help view main menu button,
        contextMenuContent:                         uiLayerConfig.contextMenu.content,
        mcBigPlayBtnStyle:                          boolean (true) -> true for media center setup for big play button style,
        mcPresPlayableFlags:                        number (OEPresentationPlayableFlags.audioAnimatableAttribute) -> OEPresentationPlayableFlags
        freePremiumView:                            boolean (true) -> true enables message dialog when user tries to use a feature not available for the current license,
        infoMessageView:                            string (OEInfoMessageViewType.none) or object {type: string, includeTargets: string or array of strings, excludeTargets: string or array of strings}
        withSubstructure:                           boolean (true) -> pdf manual version with substructure chapter,
        videoTutorial:                              string ('tutorial_video') -> localized string id for video tutorial video path
    }
    */
    modernPreTargetSetup(appConfig, target, resourceTarget, params)    {
        const MMBtnT = OEMainMenuStdBtnType;

        const defaultParams = {
            infoHelp:                   true,
            mcBigPlayBtnStyle:          true,
            mcPresPlayableFlags:        OEPresentationPlayableFlags.audioAnimatableAttribute,
            freePremiumView:            true,
            infoMessageView:            OEInfoMessageViewType.none,
            withSubstructure:           true,
            videoTutorial:              'tutorial_video'
        };

        params = Object.assign({}, defaultParams, params);

        const hasLink = typeof(appConfig.module.link) === 'string' || typeof(appConfig.module.uiLink) === 'string' || typeof(appConfig.module.linkRef) === 'string' || typeof(appConfig.module.uiLinkRef) === 'string';

        const isLicensing = config.accessType == OEFeatureAccessTypes.licensing;

        const noStartView = oeURLParameterTool.getValue(OEURLParameter.noStartView, false);
        const showMainMenu = oeURLParameterTool.getValue(OEURLParameter.showMainMenu);
        const isShowMainMenuSet = typeof(showMainMenu) === 'number';
        const autoplay = appConfig.initial ? oeURLParameterTool.getValue(OEURLParameter.autoplay, OEMediaCenterAutoplayMode.none) : OEMediaCenterAutoplayMode.none;

        let uiLayerConfig = appConfig.module.uiLayerConfig;

        let mainMenuBtnFactory = new OEMainMenuBtnConfigFactory(uiLayerConfig.mainMenuButtons);
        let helpViewFactory = new OEHelpViewConfigFactory(uiLayerConfig.helpView);
        let manualViewFactory = new OEManualViewConfigFactory(uiLayerConfig.manualView);

        //
        appConfig.target = target ? target : OETarget.effigosExplorerHelp;
        if(resourceTarget)  appConfig.resourceTarget = resourceTarget;

        //
        if(params.targetMenuEntries)    uiLayerConfig.targetMenu.entries = params.targetMenuEntries;

        //
        if(params.startViewType)    uiLayerConfig.startView.type = params.startViewType;
        uiLayerConfig.startView.targetViewShowedAtStartFlag = true;
        uiLayerConfig.startView.showAtStartUp = !noStartView && !hasLink;

        //
        mainMenuBtnFactory.replaceButtonByType(MMBtnT.info, {type: MMBtnT.help, adapter: new OEMainMenuBtnAdapterView(OEViewType.help)});
        if(params.infoHelp) mainMenuBtnFactory.insertButtonInFrontOfType(MMBtnT.help, MMBtnT.infoHelp);

        if(isLicensing) {
            mainMenuBtnFactory.insertButtonAfterType(MMBtnT.settings, {type: MMBtnT.info, icon: OEIconCodes.key, tooltip: 'main_menu_tooltip_license'});

            uiLayerConfig.infoView.isMaximized = true;
            uiLayerConfig.infoView.header = false;
            uiLayerConfig.infoView.tutorials = false;
        }

        uiLayerConfig.rightTopBar.enabled = false;
        if(!isShowMainMenuSet)   uiLayerConfig.mainMenuConfig.showInitially = true;
        uiLayerConfig.mainMenuControlBar.enabled = false;

        //
        if(params.contextMenuContent)   uiLayerConfig.contextMenu.content = params.contextMenuContent;

        //
        uiLayerConfig.widgetConfig.cutController.showHelp = false;

        //
        uiLayerConfig.mediaCenter = OEDefaultConfigFactory.mediaCenter(params.mcBigPlayBtnStyle, autoplay);
        uiLayerConfig.mediaCenter.presPlayableFlags = params.mcPresPlayableFlags;

        //
        if(params.freePremiumView)  uiLayerConfig.freePremiumView.type = OEFreePremiumViewTypes.std;

        //
        if(typeof(params.infoMessageView) === 'string') {
            uiLayerConfig.infoMessageView.type = params.infoMessageView;
        } else {
            uiLayerConfig.infoMessageView = params.infoMessageView;
        }

        //
        helpViewFactory.stdInteractiveManualManualVideo();

        //
        manualViewFactory.stdPDF(params.withSubstructure);
        if(params.videoTutorial)    manualViewFactory.setVideoTutorialLink(params.videoTutorial);
    }

    /*
    params - optional, all fields also optional
    {
        logo:                                       string (standard logo - this.logoFor(appConfig.target, config.version)) help view logo,
        startSelectionContent:                      uiLayerConfig.startSelection.content,
        startSelectionLicensingAccess:              function(sender, licensingAccess, oe) ((sender, licensingAccess, oe) => licensingAccess.isValid),
        startSelectionModuleLicensingAccess:        function(sender, module, licensingAccess, oe),
        startSelectionItemLicensingAccess:          function(sender, item, path, licensingAccess, oe),
        startSelectionShowFreePremiumView:          bool (false)
        startSelectionForHelpTarget:                bool (false)
        bottomWidgetToolbarMCWidget:                ({categoryName: 'Presentations', noImages: false, autoplay: true, forHelpTarget: false}),
        videoTutorial:                              string -> localized string id for video tutorial video path
    }
    */
    modernPostTargetSetup(appConfig, params)   {
        const MMBtnT = OEMainMenuStdBtnType;
        
        let uiLayerConfig = appConfig.module.uiLayerConfig;

        const defaultParams = {
            logo:                                   this.logoFor(appConfig.target, config.version),
            startSelectionLicensingAccess:          (sender, licensingAccess, oe) => licensingAccess.isValid,
            startSelectionShowFreePremiumView:      false,
            startSelectionForHelpTarget:            false,
            bottomWidgetToolbarMCWidget:            {categoryName: 'Presentations', noImages: false, autoplay: true, forHelpTarget: false},
        };

        params = Object.assign({}, defaultParams, params);
        params.bottomWidgetToolbarMCWidget = Object.assign({}, defaultParams.bottomWidgetToolbarMCWidget, params.bottomWidgetToolbarMCWidget);

        const isHelpTarget = appConfig.target === OETarget.effigosExplorerHelp;

        let mainMenuBtnFactory = new OEMainMenuBtnConfigFactory(uiLayerConfig.mainMenuButtons);
        let helpViewFactory = new OEHelpViewConfigFactory(uiLayerConfig.helpView);
        let manualViewFactory = new OEManualViewConfigFactory(uiLayerConfig.manualView);

        if(params.startSelectionContent) uiLayerConfig.startSelection.content = params.startSelectionContent;

        if(!isHelpTarget || params.bottomWidgetToolbarMCWidget.forHelpTarget)   uiLayerConfig.widgetConfig.bottomWidgetToolbar = OEDefaultConfigFactory.bottomWidgetToolbar(OEBottomWidgetToolbarWidgetType.mediaCenterItem, params.bottomWidgetToolbarMCWidget);

        helpViewFactory.setLogo(params.logo);
        if(!isHelpTarget)   helpViewFactory.removeTutorEntries();

        uiLayerConfig.startSelection.licensingAccess = params.startSelectionLicensingAccess;
        uiLayerConfig.startSelection.moduleLicensingAccess = params.startSelectionModuleLicensingAccess;
        uiLayerConfig.startSelection.itemLicensingAccess = params.startSelectionItemLicensingAccess;
        uiLayerConfig.startSelection.showFreePremiumView = params.startSelectionShowFreePremiumView;
        
        if((!isHelpTarget || params.startSelectionForHelpTarget) && OEFNStartSelectionModel.itemCount(uiLayerConfig.startSelection) > 1)   mainMenuBtnFactory.addButton(OEMainMenuBtnLocation.top, {type: MMBtnT.startSelection, adapter: new OEMainMenuBtnAdapterView(OEViewType.startSelection, null, null, uiLayerConfig.startSelection.licensingAccess)});

        mainMenuBtnFactory.convertToStdLayout(true);

        if(params.videoTutorial)    manualViewFactory.setVideoTutorialLink(params.videoTutorial);
    }

    //
    logoFor(target, version)  {

        switch(version) {
            case OEVersion.amboss:          return 'images/logo/logo_amboss.png';
            case OEVersion.anatomy:         return 'images/logo/logo_anatomy.png';
            case OEVersion.bayer:           return 'images/logo/logo_eye_oct.png';
            case OEVersion.bbraun:          return 'images/logo/logo_bbraun.png';
            case OEVersion.cambridgeCell:   return 'images/logo/logo_cell.png';
            case OEVersion.cell:            return 'images/logo/logo_cell.png';
            case OEVersion.demo:            return 'images/logo/logo_demo.png';
            case OEVersion.ear:             return 'images/logo/logo_ear.png';
            case OEVersion.equineHoof:      return 'images/logo/logo_equine_hoof.png';
            case OEVersion.eyeOCT:          return 'images/logo/logo_eye_oct.png';
            case OEVersion.implantcast:     return 'images/logo/logo_implantcast.png';
            case OEVersion.ionto:           return 'images/logo/logo_skin_2.png';
            case OEVersion.knowOn:          return 'images/logo/logo_know_on.png';
            case OEVersion.mediCADemy:      return 'images/logo/logo_medicademy.png';
            case OEVersion.ortho:           return 'images/logo/logo_ortho.png';
            case OEVersion.school:          return 'images/logo/logo_school.png';
            case OEVersion.science:         return 'images/logo/logo_science.png';
            case OEVersion.skin:            return 'images/logo/logo_skin_2.png';
            case OEVersion.sport:           return 'images/logo/logo_sport.png';
            case OEVersion.surgery:         return 'images/logo/logo_surgery.png';
            case OEVersion.thieme:          return 'images/logo/logo_thieme.png';
            case OEVersion.toxnetz:         return 'images/logo/logo_toxnetz.png';
            case OEVersion.xRay:            return 'images/logo/logo_xray.png';
            case OEVersion.xRayMin:         return 'images/logo/logo_xray.png';
            case OEVersion.xRayIcarus:      return 'images/logo/logo_xray.png';
            case OEVersion.zeissOpti:       return 'images/logo/logo_zeiss_opti.png';
        }

        switch(target)   {
            case OETarget.humanSkull:       return 'images/logo/logo_skull.png';

            case OETarget.humanEarMacro:
            case OETarget.humanEarInner:
            case OETarget.humanEarMicro:    return 'images/logo/logo_ear.png';

            case OETarget.humanSkinMacro:
            case OETarget.humanSkinMicro:   return 'images/logo/logo_skin.png';

            case OETarget.humanEyeMacro:
            case OETarget.humanEyeMicro:    return 'images/logo/logo_eye.png';

            case OETarget.animalSnail:      return 'images/logo/logo_snail.png';

            case OETarget.humanKnee:        return 'images/logo/logo_knee.png';

            case OETarget.zeissOpti:
            case OETarget.zeissOpti2:
            case OETarget.zeissUV:          return 'images/logo/logo_zeiss_opti.png';

            case OETarget.humanFoot:        return 'images/logo/logo_foot.png';

            case OETarget.hoofDynamics:
            case OETarget.hoofVessels:
            case OETarget.hoofWeb:
            case OETarget.hoofMicro:        return 'images/logo/logo_hoof.png';

            case OETarget.animalCell:
            case OETarget.plantCell:        return 'images/logo/logo_animal_cell.png';
        }

        return 'images/logo/logo.png';
    }

    //
    selectTarget(target, info) {
        info.targetChoiceTag = 0;

        let translateFn = (target) => {
            if(target === OETarget.humanEye)    return OETarget.humanEye2;
            return target;
        };

        // target is already choosen so just return
        if(typeof(target) === 'string') return translateFn(target);
        
        // if a target is provided via url parameter, translate this target to an OETarget and return
        let targetUrlParam = oeURLParameterTool.getValue(OEURLParameter.target);
        if(typeof(targetUrlParam) === 'string') {
            info.targetChoiceTag = 2;

            switch(targetUrlParam) {
                case 'foot':        return OETarget.humanFoot;
                case 'hoof':        return OETarget.hoofWeb;

                case 'ear':
                case 'earMacro':    return OETarget.humanEarMacro;

                case 'earMicro':    return OETarget.humanEarMicro;

                case 'eye':
                case 'eyeMacro':
                case 'eyeRes':      return OETarget.humanEyeMacro;

                case 'eyeMicro':    return OETarget.humanEyeMicro;
                case 'eyeCornea':   return OETarget.humanEyeCornea;
                case 'knee':        return OETarget.humanKnee;
            }

            return translateFn(targetUrlParam);
        }

        // if config.target is set, i.e., the target is fixed by the build configuration, translate this target to a oe target and return
        if(typeof(config.target) === 'string' && config.target !== '')   {
            info.targetChoiceTag = 1;

            switch(config.target) {
                case 'humanEar': return OETarget.humanEarMacro;
            }

            return translateFn(config.target);
        }
    }

    //
    selectResourceTarget(target)    {
        if(config.target == OETarget.humanEye) {
            return OEResourceTarget.humanEyeRes;
        }

        if(config.target == 'humanEar') {
            return OEResourceTarget.humanEarRes;
        }

        if(OEResourceTarget.isResourceTarget(config.target))    return config.target;
    
        if(target == OETarget.sensorySystemsEar || target == OETarget.sensorySystemsEye)  {
            return OETarget.sensorySystems;
        }
    
        return target;
    }

    //
    setupTargetMenuConfig(targetMenuConfig, target) {
        let isLicensingStart = config.version === OEVersion.licensingStart || config.version === OEVersion.effigos;

        // versions configs besides licensingStart and effigos take care of it on their own
        if(!!config.version && !isLicensingStart)    return;

        //
        targetMenuConfig.enabled = false;

        // legacy non version multi target versions for human ear and eye
        if(config.target == OETarget.humanEye || config.target == OEResourceTarget.humanEyeRes) {
            targetMenuConfig.enabled = true;
            targetMenuConfig.entries = OETargetMenuDefs.humanEyeNew;
            return;
        }

        if(config.target == 'humanEar' || config.target == OEResourceTarget.humanEarRes) {
            targetMenuConfig.enabled = true;
            targetMenuConfig.entries = OETargetMenuDefs.humanEar;
            return;
        }

        // allow for inter target switching (AppMode.play) for ear and eye targets for licensing start versions
        if(isLicensingStart) {
            switch(target) {
                case OETarget.humanEarMacro:
                case OETarget.humanEarMicro:
                case OETarget.humanEarInner:
                case OETarget.sensorySystemsEar:
                    targetMenuConfig.enabled = true;
                    targetMenuConfig.entries = OETargetMenuDefs.humanEar;
                    return;
                case OETarget.humanEyeMacro:
                case OETarget.humanEyeMicro:
                case OETarget.humanEyeCornea:
                case OETarget.sensorySystemsEye:
                    targetMenuConfig.enabled = true;
                    targetMenuConfig.entries = OETargetMenuDefs.humanEye;
                    return;
            }
        }

        // legacy non version & licensing start version's multi target switching for skin, hoof, cell
        switch(target) {
            case OETarget.humanSkinMacro:
            case OETarget.humanSkinMicro:
                targetMenuConfig.enabled = true;
                targetMenuConfig.entries = OETargetMenuDefs.humanSkin;
                return;
            case OETarget.hoofWeb:
            case OETarget.hoofVessels:
            case OETarget.hoofDynamics:
            case OETarget.hoofMicro:
            case OETarget.hoofCarpal:
                targetMenuConfig.enabled = true;
                targetMenuConfig.entries = OETargetMenuDefs.hoof;
                return;
            case OETarget.animalCell:
            case OETarget.plantCell:
                targetMenuConfig.enabled = true;
                targetMenuConfig.entries = OETargetMenuDefs.cell;
                return;
        }
    }
}

export default new AppConfigHelper();